/**
 * usePaymentStatusPanel - the live payment status panel reading
 * (docs/POS_PAYMENT_STATUS_PANEL.md, docs/CHECKOUT_API_2026-10-28.md §5).
 *
 * Asks the server on mount, every 60 s, when the window gets focus, when the
 * tab becomes visible and when the device comes back online. The server asks
 * Aster Gateway at most every 30 s per restaurant, so this is cheap.
 *
 *   const status = usePaymentStatusPanel(checkoutPanelFetcher({ token, terminalId }));
 *   <PaymentStatusPanel status={status} canManage />
 *
 * The reading is the gateway's own (never a guess): a failed call keeps the
 * last reading and says so; a reading older than 30 minutes is Unknown.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

export const PANEL_REFRESH_MS = 60 * 1000;
const STALE_MS = 30 * 60 * 1000;

const tokenOf = (t) => (typeof t === 'function' ? t() : t);

function errorText(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (error?.response?.status === 401 || error?.response?.status === 403) return 'Not signed in on this device.';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'This device is offline.';
  return 'Could not reach chain29 to check the payment status.';
}

/** Staff register / back office: POST /api/checkout/terminals/status (Bearer token). */
export function checkoutPanelFetcher({ token, role = 'pos_register', terminalId = null, baseUrl = API_BASE_URL } = {}) {
  return async () => {
    const params = {};
    if (role) params.role = role;
    if (terminalId) params.terminal_id = terminalId;
    const res = await axios.post(`${baseUrl}/api/checkout/terminals/status`, null, {
      params, headers: { Authorization: `Bearer ${tokenOf(token)}` },
    });
    return res.data;
  };
}

/** Kiosk: POST /api/kiosk/{device_id}/terminal/status (device token). Adds `payment` (readiness). */
export function kioskPanelFetcher({ deviceId, deviceToken, baseUrl = API_BASE_URL } = {}) {
  return async () => {
    const res = await axios.post(`${baseUrl}/api/kiosk/${deviceId}/terminal/status`, null, {
      headers: { Authorization: `Device ${tokenOf(deviceToken)}` },
    });
    return res.data;
  };
}

/** The terminal the pill speaks for: this register's, or the only one. */
export function thisTerminal(panel) {
  const list = panel?.terminals || [];
  return list.find((t) => t.is_this_register) || (list.length === 1 ? list[0] : null);
}

/**
 * The header pill's state: {tone: online|offline|unknown|checking, label, reason, count}.
 * Green only when this register's terminal is online (spec, "Rules").
 */
export function summarizePanel(panel, { checking = false, error = null } = {}) {
  if (!panel) {
    if (checking) return { tone: 'checking', label: 'Checking...', reason: 'Asking Aster Gateway for the terminal status.', count: null };
    return { tone: 'unknown', label: 'Unknown', reason: error || 'Not checked yet.', count: null };
  }
  const count = panel.card_terminal_count ?? (panel.terminals || []).length;
  // a background refresh keeps showing the last reading (the dropdown shows the check in flight)
  const checkedAt = Date.parse(panel.checked_at || '');
  if (error && Number.isFinite(checkedAt) && Date.now() - checkedAt > STALE_MS) {
    return { tone: 'unknown', label: 'Unknown', reason: 'Status is old - the last reading is more than 30 minutes old.', count };
  }
  if (!count) return { tone: 'unknown', label: 'Unknown', reason: 'No card terminal is set up for this restaurant.', count };
  const mine = thisTerminal(panel);
  if (!mine) return { tone: 'unknown', label: 'Unknown', reason: 'No card terminal is chosen for this register.', count };
  const reason = [mine.reason, error ? `(${error})` : null].filter(Boolean).join(' ');
  if (mine.state === 'online') return { tone: 'online', label: 'Online', reason: reason || 'Aster Gateway reports the card terminal online.', count, terminal: mine };
  if (mine.state === 'offline') return { tone: 'offline', label: 'Offline', reason: reason || 'Aster Gateway reports the card terminal offline.', count, terminal: mine };
  return { tone: 'unknown', label: 'Unknown', reason: reason || 'Aster Gateway did not report this terminal.', count, terminal: mine };
}

export function usePaymentStatusPanel(fetchPanel, { intervalMs = PANEL_REFRESH_MS, enabled = true } = {}) {
  const [panel, setPanel] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [lastAskedAt, setLastAskedAt] = useState(null);
  const fetchRef = useRef(fetchPanel);
  fetchRef.current = fetchPanel;
  const inFlight = useRef(false);
  const again = useRef(false);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    if (!fetchRef.current) return;
    if (inFlight.current) { again.current = true; return; } // asked again mid-flight (e.g. another terminal chosen): one more read after
    again.current = false;
    inFlight.current = true;
    setChecking(true);
    try {
      const data = await fetchRef.current();
      if (!alive.current) return;
      setPanel(data);
      setError(null);
    } catch (e) {
      if (alive.current) setError(errorText(e));
    } finally {
      inFlight.current = false;
      if (alive.current) {
        setChecking(false);
        setLastAskedAt(new Date().toISOString());
        if (again.current) setTimeout(() => refresh(), 0);
      }
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(refresh, intervalMs);
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, intervalMs, refresh]);

  const summary = useMemo(() => summarizePanel(panel, { checking, error }), [panel, checking, error]);
  return { panel, checking, error, refresh, summary, lastAskedAt };
}

export default usePaymentStatusPanel;
